import { useEffect, useState } from 'react';
import { RasterOverlayAdapter, PointQueryAdapter } from '../adapters';
import { useMapperPluginConfig } from '../mapperConfig';

interface AnalysisOverlayPanelProps {
  onOpacityChange?: (opacity: number) => void;
}

/** L5 — ISRIC soil overlay controls (EPSG:3857 WMS when supported). */
export default function AnalysisOverlayPanel({ onOpacityChange }: AnalysisOverlayPanelProps) {
  const { data: configData } = useMapperPluginConfig();
  const analysis = configData?.config?.layers?.analysis;
  const [opacity, setOpacity] = useState(0.6);
  const [dataset, setDataset] = useState('phh2o');
  const [supports3857, setSupports3857] = useState<boolean | null>(null);

  useEffect(() => {
    if (!analysis?.enabled) return;
    RasterOverlayAdapter.checkIsricCapabilities().then((r) => setSupports3857(r.supports_epsg_3857 ?? null));
  }, [analysis?.enabled]);

  if (!analysis?.enabled) return null;

  const handleOpacity = (v: number) => {
    setOpacity(v);
    onOpacityChange?.(v);
  };

  return (
    <div className="border-t border-gray-100 px-4 py-3 space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Analysis overlays</h3>
      <select
        className="w-full rounded border border-gray-200 px-2 py-1 text-xs"
        value={dataset}
        onChange={(e) => setDataset(e.target.value)}
      >
        <option value="phh2o">Soil pH (ISRIC)</option>
        <option value="soc">Organic carbon (ISRIC)</option>
        <option value="clay">Clay content (ISRIC)</option>
      </select>
      <label className="flex items-center gap-2 text-xs text-gray-600">
        Opacity
        <input
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={opacity}
          onChange={(e) => handleOpacity(Number(e.target.value))}
          className="flex-1"
        />
      </label>
      {supports3857 != null && (
        <p className="text-[10px] text-gray-400">
          {supports3857 ? 'WMS EPSG:3857 — no reprojection needed' : 'May require CRS reprojection'}
        </p>
      )}
      <button
        type="button"
        className="text-xs text-teal-600 hover:underline"
        onClick={() => PointQueryAdapter.query(dataset, -30.5, 25.0)}
      >
        Test point query (best-effort)
      </button>
    </div>
  );
}

export function getIsricOverlayUrl(property: string, supports3857: boolean | null) {
  return RasterOverlayAdapter.isricWmsUrl(property, supports3857 !== false);
}
